import {
  Children,
  Component,
  createElement,
  CSSProperties,
  PropTypes,
  ReactElement,
} from "react";
import {connect} from "react-redux";

import {RootState} from "src/reducers";
import {Enhancer} from "src/utils/components";

export type LanguageTranslation = {
  [key: string]: string;
};

export type Translation = {
  [language: string]: LanguageTranslation;
};

export type TranslationFunction = (value: string) => string;

interface TranslationContext {
  addChildComponent(component: ComponentWithTranslationContext<any>): void;
  removeChildComponent(component: ComponentWithTranslationContext<any>): void;
  getTranslationFunction(language: string): TranslationFunction;
}

const translationContextTypes = {
  addChildComponent: PropTypes.func,
  removeChildComponent: PropTypes.func,
  getTranslationFunction: PropTypes.func,
};

export interface TranslationProviderProps {
  translation: Translation;
  children?: ReactElement<any>;
}

declare const process: any;

export class TranslationFunctionsCache {
  translation: Translation;
  translationFunctions: {[language: string]: TranslationFunction} = {};

  constructor(translation: Translation) {
    this.translation = translation;
  }

  get(language: string) {
    const {translation} = this;
    const languageTranslation = translation[language] || {};

    if (!this.translationFunctions[language]) {
      this.translationFunctions[language] = (value: string) => {
        const translated = languageTranslation[value] || value;
        if (language !== 'en' && !languageTranslation[value]) {
          const message = `Missing translation for key '${value}' ` +
                          `for language '${language}'`;
          if (process.env.NODE_ENV === 'development') {
            console.error(message);
          }
        }
        return translated;
      };
    }
    return this.translationFunctions[language];
  }
}

export const TranslationProvider = connect(
  (state: RootState, props: TranslationProviderProps) => {
    // const {language} = state.context;  // todo
    return {language: 'en'};
  },
)(class TranslationProvider extends Component<TranslationProviderProps & {
  language: string;
}, void> {
  static childContextTypes = translationContextTypes;

  private childComponents: ComponentWithTranslationContext<any>[] = [];

  private translationFunctionsCache =
    new TranslationFunctionsCache(this.props.translation);

  addChildComponent = (component: ComponentWithTranslationContext<any>) => {
    this.childComponents.push(component);
    component.setLanguage(this.props.language);
  }

  removeChildComponent = (component: ComponentWithTranslationContext<any>) => {
    const index = this.childComponents.indexOf(component);
    if (index !== -1)
      this.childComponents.splice(index, 1);
  }

  getChildContext(): TranslationContext {
    const {
      addChildComponent,
      removeChildComponent,
      getTranslationFunction,
    } = this;
    return {
      addChildComponent,
      removeChildComponent,
      getTranslationFunction,
    };
  }

  componentWillReceiveProps(nextProps: {language: string}) {
    const {language} = nextProps;

    for (const child of this.childComponents)
      child.setLanguage(language);
  }

  getTranslationFunction = (language: string) => {
    return this.translationFunctionsCache.get(language);
  }

  render() {
    return Children.only(this.props.children);
  }
});

class ComponentWithTranslationContext<P> extends Component<P, {
  language: string;
}> {
  static contextTypes = translationContextTypes;

  getContext() {
    return this.context as TranslationContext;
  }

  setLanguage(language: string) {
    this.setState({language});
  }

  componentWillMount() {
    this.getContext().addChildComponent(this);
  }

  componentWillUnmount() {
    this.getContext().removeChildComponent(this);
  }
}

export const withTranslation: Enhancer<{getTranslation: TranslationFunction}> =
  (component: any) => class ComponentWithTranslation
  extends ComponentWithTranslationContext<any> {
    render() {
      const getTranslation: TranslationFunction =
        this.getContext().getTranslationFunction(this.state.language);

      return createElement(component, {...this.props, getTranslation});
    }
  };

export const withLanguage: Enhancer<{language: string}> = (component: any) =>
  class ComponentWithLanguage extends ComponentWithTranslationContext<any> {
    render() {
      return createElement(component, {
        ...this.props,
        language: this.state.language,
      });
    }
  };

export interface TextProps {
  style?: CSSProperties;
}

export const Text = withTranslation<TextProps>(
  ({getTranslation, style, children}) => {
  const key = Array.isArray(children)
    ? children.join(' ')
    : children as string;

  return <span style={style}>{getTranslation(key)}</span>;
});



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/translation.tsx